import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth.js';
import { requestOtp, verifyOtp } from '../api/auth.js';

type Step = 'mobile' | 'otp';

/** Backend requires E.164; accept bare Indian 10-digit input and add +91. */
function normalizeMobile(raw: string): string {
  const v = raw.replace(/[\s-]/g, '');
  if (/^\d{10}$/.test(v)) return `+91${v}`;
  if (/^91\d{10}$/.test(v)) return `+${v}`;
  return v;
}

const inputCls =
  'w-full bg-surface2 border border-white/10 rounded-lg px-3 py-2 text-sm text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleMobileSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const normalized = normalizeMobile(mobile);
      setMobile(normalized);
      await requestOtp(normalized);
      setStep('otp');
    } catch (err: unknown) {
      const msg = extractError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await verifyOtp(mobile, otp);
      login(res.accessToken, res.refreshToken, res.user);
      navigate('/device-plane', { replace: true });
    } catch (err: unknown) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-page">
      <div className="w-full max-w-sm bg-surface border border-white/10 rounded-xl p-8">
        <h1 className="text-2xl font-bold text-ink mb-1">M8 Device Plane</h1>
        <p className="text-sm text-muted mb-6">Haron middleware observability</p>

        {step === 'mobile' ? (
          <form onSubmit={handleMobileSubmit} className="space-y-4">
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-ink2 mb-1">
                Mobile number
              </label>
              <input
                id="mobile"
                type="tel"
                placeholder="+919700000000"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className={inputCls}
                required
              />
            </div>
            {error && <p className="text-sm text-badink">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-white py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {loading ? 'Sending OTP…' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <p className="text-sm text-ink2">
              OTP sent to <span className="font-medium text-ink">{mobile}</span>
            </p>
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-ink2 mb-1">
                Enter OTP
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                placeholder="6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                className={inputCls}
                required
              />
            </div>
            {error && <p className="text-sm text-badink">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-white py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {loading ? 'Verifying…' : 'Verify & Login'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('mobile'); setOtp(''); setError(''); }}
              className="w-full text-sm text-muted hover:text-ink2"
            >
              ← Change number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function extractError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const r = (err as { response?: { data?: { message?: string; code?: string } } }).response;
    return r?.data?.message ?? r?.data?.code ?? 'Request failed';
  }
  return 'Something went wrong';
}
